/**
 * board-list/constants — kind→icon / kind→label maps, the canonical rail
 * section order, and the department grouping tables (order, labels, options,
 * kind→department map).
 *
 * Extracted VERBATIM from the former KanbanBoardList monolith during the
 * <500-line decomposition. The public maps (ICON_BY_KIND, SHORT_LABEL_BY_KIND,
 * BOARD_KIND_RAIL_ORDER, DEPARTMENT_ORDER, DEPARTMENT_LABEL, DEPARTMENT_OPTIONS,
 * KIND_TO_DEPARTMENT) are re-exported from `KanbanBoardList.tsx` so existing
 * consumers + the package barrels resolve unchanged. `LABEL_BY_KIND` stays
 * package-internal (consumed by the rail's sections + helpers). No behavioural
 * change.
 */
import {
  Briefcase, Inbox, Receipt, Layers, FileText, Send, Square, Users,
  AlertOctagon, AlertTriangle, RefreshCw,
  // FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — per-kind glyphs for the
  // eight construction department boards.
  ShoppingCart, HardHat, ShieldAlert, BadgeCheck, Truck, FileStack,
  UserPlus, Gavel,
} from 'lucide-react'

import type { BoardDepartment, BoardKind } from '../../types'

// Exported so the kanban-host's icon-only collapsed rail can render
// the same per-kind glyph the full-width rail uses (DRY: a single
// source of truth keeps the icon rail visually congruent with the
// expanded rail). See packages/kanban-host/src/KanbanPage.tsx —
// `<KanbanBoardIconRail>` consumes this map.
export const ICON_BY_KIND: Record<BoardKind, React.ComponentType<{ className?: string }>> = {
  INBOX: Inbox,
  JOB: Briefcase,
  // Use the same Briefcase icon for the org-wide pipeline as for the
  // per-job detail boards — they're conceptually the same family
  // (just one zoomed-out view of every Job vs one detail-board per Job).
  JOBS_PIPELINE: Briefcase,
  QUOTE_PIPELINE: FileText,
  VARIATION_LOG: Layers,
  AR_AP: Receipt,
  SENT_ITEMS: Send,
  // FEAT-2026-05-21-KANBAN-WORKFLOW-FOUNDATION — pre-quote lead funnel.
  CRM_PIPELINE: Users,
  CUSTOM: Square,
  // Trello-mirrored boards reuse the Layers icon — see
  // KanbanBoardGallery for the rationale.
  TRELLO_MIRROR: Layers,
  // Stack 13 MR-A — Error Log Live. AlertOctagon signals "errors
  // quarantined here" at a glance; complements the BE-side
  // catalogue icon hint ("bell-off") with a more semantically
  // direct cue for the operator. Pinned to the bottom of the
  // rail via the `order` array below.
  SYSTEM: AlertOctagon,
  // FEAT-2026-06-01-KANBAN-TEAM-BOARD — people roster. Reuses the
  // Users icon (shared with CRM; they render in separate sections).
  TEAM: Users,
  // FEAT-2026-06-02-KANBAN-OPERATIONS-BOARD — triage-critical ops
  // (payment failures, suspensions, renewals). AlertTriangle signals
  // "act on this" without the admin-only quarantine connotation of
  // AlertOctagon (SYSTEM).
  OPERATIONS: AlertTriangle,
  // FEAT-2026-06-02-KANBAN-OVERHEADS-SUBBOARD — recurring overhead
  // spend / renewals. RefreshCw conveys the recurring nature.
  OVERHEADS: RefreshCw,
  // FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — construction department boards.
  PROCUREMENT: ShoppingCart,
  SUBCONTRACTORS: HardHat,
  SAFETY: ShieldAlert,
  QUALITY: BadgeCheck,
  PLANT: Truck,
  DOCUMENTS: FileStack,
  HR: UserPlus,
  TENDERS: Gavel,
}

// BUG-2026-07-07-KANBAN-RAIL-SELECT-DEFAULTS — the canonical, auto-provisioned
// "system / default" board kinds. These are the org-level singletons the
// backend seeds and machine-manages — the personal Inbox / Sent Items, the
// canonical AR/AP + Quote + Jobs + CRM pipelines + Variation log, and the
// fully machine-driven System error log / Team roster / Operations / Overheads
// boards. Operators don't hand-curate or bulk-archive these (archiving the
// org Inbox by accident would be a foot-gun), so the rail + gallery must HIDE
// the multi-select checkbox AND the hover-archive icon on them. Archive is
// still reachable for these via the right-click context menu.
//
// Everything NOT in this set is selectable: CUSTOM (user boards), per-JOB
// boards, TRELLO_MIRROR imports, and the operator-created construction
// department boards (Procurement / Subcontractors / Safety / Quality / Plant /
// Documents / HR / Tenders). Operator report (2026-07-07): *"the selection I
// only see in custom boards!! I should be able to see it in all boards other
// than the defaults"* + *"I still can't select using the checkbox — it opens
// up the board!!!"*.
//
// Typed as a Set<BoardKind> literal so adding a new BoardKind forces an
// explicit selectable-vs-default decision at review time (same drift-guard
// posture as ICON_BY_KIND / KIND_TO_DEPARTMENT above).
export const SYSTEM_DEFAULT_BOARD_KINDS: ReadonlySet<BoardKind> = new Set<BoardKind>([
  'INBOX',
  'SENT_ITEMS',
  'JOBS_PIPELINE',
  'QUOTE_PIPELINE',
  'CRM_PIPELINE',
  'VARIATION_LOG',
  'AR_AP',
  'OVERHEADS',
  'SYSTEM',
  'TEAM',
  'OPERATIONS',
])

/**
 * True when a board is an auto-provisioned system/default org board — the rail
 * + gallery hide the multi-select checkbox and the hover-archive affordance on
 * these (see SYSTEM_DEFAULT_BOARD_KINDS). Tolerates null / arbitrary strings so
 * callers can pass ``board.kind`` straight through (an unknown/absent kind is
 * treated as a NON-default, i.e. selectable, board — fail-open so a new kind
 * the FE hasn't mapped yet still gets the selection affordance rather than
 * silently losing it).
 *
 * f(kind) -> boolean.
 */
export function isSystemDefaultBoardKind(
  kind: BoardKind | string | null | undefined,
): boolean {
  return !!kind && SYSTEM_DEFAULT_BOARD_KINDS.has(kind as BoardKind)
}

// Section labels for the rail. The gallery has its own equivalent
// in KanbanBoardGallery.tsx — keep both in sync when adding a kind.
//
// TRELLO_MIRROR is intentionally NOT relabelled to "Trello" any more —
// per qb!2756 every bridged board is reclassified to its functional
// kind (JOB / INBOX / CUSTOM) on next bridge run, so this kind is now
// only a fallback for un-classifiable boards. Show them as "Other"
// rather than advertising the source system: operator complaint
// 2026-05-11: *"why did you label it trello? We are just moving data
// it should be labeled as job or whatever properly process this"*.
export const LABEL_BY_KIND: Partial<Record<BoardKind, string>> = {
  TRELLO_MIRROR: 'Other',
  // Stack 13 MR-A — display the SYSTEM section as "System log"
  // (rather than the default "SYSTEM" all-caps from the union
  // key). Sits visually below operator workflow boards.
  SYSTEM: 'System log',
  // FEAT-2026-06-01-KANBAN-TEAM-BOARD — people roster section header.
  TEAM: 'Team',
  // Finance section header — without this override the rail showed the
  // raw "AR AP" (from kind.replace('_',' ')). Match the gallery's
  // operator-facing "Cashflow (AR/AP)" label so the finance board is
  // findable by name.
  AR_AP: 'Cashflow (AR/AP)',
  // FEAT-2026-06-02-KANBAN-OPERATIONS-BOARD — operator-facing label.
  OPERATIONS: 'Operations',
  // FEAT-2026-06-02-KANBAN-OVERHEADS-SUBBOARD — operator-facing label.
  // (Usually renders nested under AR/AP via parent_board; this label
  // covers the rare top-level case.)
  OVERHEADS: 'Overheads & Renewals',
  // FEAT-2026-06-07-KANBAN-CONSTRUCTION — construction-friendly section
  // labels. The bare union keys ("QUOTE PIPELINE", "CRM PIPELINE",
  // "VARIATION LOG") read like internal enums; these match how a
  // builder thinks about the work.
  QUOTE_PIPELINE: 'Quotes & estimating',
  CRM_PIPELINE: 'Leads & tenders',
  VARIATION_LOG: 'Variations',
  // FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — operator-facing section
  // headers (the bare union keys like "PLANT" / "HR" read like enums).
  PROCUREMENT: 'Procurement',
  SUBCONTRACTORS: 'Subcontractors',
  SAFETY: 'Safety & WHS',
  QUALITY: 'Quality & Defects',
  PLANT: 'Plant & Equipment',
  DOCUMENTS: 'Documents & RFIs',
  HR: 'HR & Recruitment',
  TENDERS: 'Tenders & Bids',
}

// Tile captions for the COLLAPSED board-kind strip only. That strip
// truncates a kind's full label to its FIRST WORD, which made the two
// Delivery kinds read as near-duplicate tiles — JOBS_PIPELINE ("Jobs
// Pipeline" → "Jobs") sat directly above JOB ("Job"), so the rail showed
// "Jobs" then "Job" and looked broken (operator 2026-06-21: "why are there
// two jobs and job, do better naming"). These explicit short captions
// disambiguate the tiles ("Pipeline" = the org-wide Jobs Pipeline board;
// "Jobs" = the per-job detail boards) while the fly-out/tooltip keep the
// full LABEL_BY_KIND name. Only override where first-word truncation is
// ambiguous; everything else falls through to the humanized label.
export const SHORT_LABEL_BY_KIND: Partial<Record<BoardKind, string>> = {
  JOBS_PIPELINE: 'Pipeline',
  JOB: 'Jobs',
}

// FEAT-2026-06-07-KANBAN-BOARD-SWITCHER — canonical rail/section
// ordering of board kinds, lifted to module scope + exported so the
// top-bar board switcher (KanbanBoardSwitcherMenu) orders its category
// fly-outs identically to this left rail. Keep in sync with the inline
// `order` array below (which now references it) and the gallery's
// KIND_ORDER.
// Within-department order is what matters here — the rail/gallery regroup
// kinds by DEPARTMENT_ORDER via groupKindsByDepartment, so a kind's slot in
// this list only sets its position among its department peers. Ordered to
// read as the construction lifecycle: Sales → Estimating → Delivery →
// Procurement → Finance → People → Safety → Quality → Assets → Documents →
// Ops. FEAT-2026-06-09 adds the eight construction kinds.
export const BOARD_KIND_RAIL_ORDER: BoardKind[] = [
  'INBOX', 'CRM_PIPELINE', 'TENDERS', 'QUOTE_PIPELINE',
  'JOBS_PIPELINE', 'JOB', 'VARIATION_LOG',
  'PROCUREMENT', 'SUBCONTRACTORS',
  'AR_AP', 'OVERHEADS', 'SENT_ITEMS',
  'TEAM', 'HR',
  'SAFETY', 'QUALITY', 'PLANT', 'DOCUMENTS',
  'OPERATIONS', 'CUSTOM',
  'TRELLO_MIRROR',
  'SYSTEM',
]

// High-volume / low-signal kinds. They sit at the tail of their department in
// both surfaces (a busy tenant carries 100+ per-JOB boards, and TRELLO_MIRROR /
// SYSTEM are unclassified or quarantined noise), and
// FEAT-2026-07-28-KANBAN-SECTION-ACTIVITY-RANK also keeps them OUT of the
// department's activity score. Without that second exclusion, the department
// holding the job boards would rank first permanently and the ranking would
// carry no information. Lives here, not in the gallery, because the rail needs
// the same list and must not import from board-gallery.
export const TAIL_KINDS: BoardKind[] = ['JOB', 'TRELLO_MIRROR', 'SYSTEM']

// FEAT-2026-06-08-KANBAN-DEPARTMENT — the higher-level business grouping
// above kind, mirrored from the backend BoardDepartment enum +
// KIND_DEPARTMENT_MAP (email_kanban/models/board.py). Lifted to module
// scope + exported so the rail, gallery and collapsed icon strip group
// boards into the SAME business functions and never drift. `kind` answers
// "what shape is this board"; `department` answers "which part of the
// business owns it" (e.g. AR_AP + OVERHEADS + SENT_ITEMS → Finance).
export const DEPARTMENT_ORDER: BoardDepartment[] = [
  'SALES', 'ESTIMATING', 'DELIVERY', 'PROCUREMENT', 'FINANCE',
  'PEOPLE', 'SAFETY', 'QUALITY', 'ASSETS', 'DOCUMENTS', 'OPERATIONS',
]

export const DEPARTMENT_LABEL: Record<BoardDepartment, string> = {
  SALES: 'Sales & CRM',
  ESTIMATING: 'Estimating & Quotes',
  DELIVERY: 'Project Delivery',
  PROCUREMENT: 'Procurement & Suppliers',
  FINANCE: 'Finance & Cashflow',
  PEOPLE: 'People & HR',
  SAFETY: 'Safety & Compliance',
  QUALITY: 'Quality & Defects',
  ASSETS: 'Plant & Equipment',
  DOCUMENTS: 'Documents & RFIs',
  OPERATIONS: 'Operations & Admin',
}

// FEAT-2026-06-15-KANBAN-BOARD-CATEGORY-MOVE — the ordered {value,label}
// choice list the board context menu's "Move to category…" submenu
// renders. Passed in as a prop so the menu never imports the rail (no
// import cycle); single source of truth stays here with DEPARTMENT_ORDER.
export const DEPARTMENT_OPTIONS: { value: BoardDepartment; label: string }[] =
  DEPARTMENT_ORDER.map((d) => ({ value: d, label: DEPARTMENT_LABEL[d] }))

// Every BoardKind maps to exactly one department. Typed as a total
// Record so adding a new BoardKind (later construction phases) is a
// compile error until it's slotted into a department — the same drift
// guard ICON_BY_KIND already provides.
export const KIND_TO_DEPARTMENT: Record<BoardKind, BoardDepartment> = {
  INBOX: 'OPERATIONS',
  JOB: 'DELIVERY',
  JOBS_PIPELINE: 'DELIVERY',
  VARIATION_LOG: 'DELIVERY',
  QUOTE_PIPELINE: 'ESTIMATING',
  CRM_PIPELINE: 'SALES',
  AR_AP: 'FINANCE',
  OVERHEADS: 'FINANCE',
  SENT_ITEMS: 'FINANCE',
  TEAM: 'PEOPLE',
  OPERATIONS: 'OPERATIONS',
  SYSTEM: 'OPERATIONS',
  CUSTOM: 'OPERATIONS',
  TRELLO_MIRROR: 'OPERATIONS',
  // FEAT-2026-06-09-KANBAN-CONSTRUCTION-KINDS — mirror the backend
  // KIND_DEPARTMENT_MAP (Subcontractors→Procurement, HR→People,
  // Tenders→Sales) so the FE grouping matches the server department.
  PROCUREMENT: 'PROCUREMENT',
  SUBCONTRACTORS: 'PROCUREMENT',
  SAFETY: 'SAFETY',
  QUALITY: 'QUALITY',
  PLANT: 'ASSETS',
  DOCUMENTS: 'DOCUMENTS',
  HR: 'PEOPLE',
  TENDERS: 'SALES',
}

// FEAT-2026-05-20-TIMEBOARD / FEAT-2026-05-21-TIMEBOARD-AUTO-LANES — the
// canonical Gantt day-bucket lanes the timeMode mutation idempotently
// provisions when an operator flips a board into Time Board Mode. Names +
// colors mirror BoardGanttBucketView's bucket spec so the Board view and
// the Gantt view look identical out of the box. Module-scoped so the
// mutation hook (useBoardListMutations) references one shared source.
export const TIME_BOARD_LANE_TEMPLATES: Array<{ name: string; color: string }> = [
  { name: 'Overdue',     color: '#EF4444' },
  { name: 'Today',       color: '#F59E0B' },
  { name: 'Tomorrow',    color: '#14B8A6' },
  { name: 'This week',   color: '#10B981' },
  { name: 'Next week',   color: '#10B981' },
  { name: 'Later',       color: '#A855F7' },
  { name: 'Unscheduled', color: '#94A3B8' },
]
